import { ActionType, ProTable } from "@ant-design/pro-components";
import { Avatar, Button, Form, message, Modal, Popconfirm, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useGlobalState } from "../../components/global";
import { useEffect, useRef } from "react";
import GlobalWrap from "../../components/globalWrap";
import UserRoleForm from "./userRoleForm";
import moment from "moment";
import UUIDComponent from "../../components/UUIDComponent";
import UserForm from "./UserForm";

export default function UserList() {
    const { jsonRequest } = useGlobalState();
    const tableRef = useRef<ActionType>();
    const [form] = Form.useForm();

    useEffect(() => {
        tableRef.current?.reload();
    }, []);

    const columns: any[] = [
        {
            title: "用户ID",
            dataIndex: "id",
            search: false,
            align: "center",
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "用户名",
            dataIndex: "userName",
            search: false,
            align: "center",
        },
        {
            title: "昵称",
            dataIndex: "nickName",
            search: false,
            align: "center",
        },
        {
            title: "头像",
            dataIndex: "avatar",
            search: false,
            align: "center",
            render: (_: any, record: any) => <Avatar src={record?.url} size={50} />
        },
        {
            title: "邮箱",
            dataIndex: "email",
            search: false,
            align: "center",
        },
        {
            title: "手机号",
            dataIndex: "phone",
            search: false,
            align: "center",
        },
        {
            title: "性别",
            dataIndex: "sex",
            search: false,
            align: "center",
            render: (text: string) => (
                <div>{text === 'MAN' ? '男' : text === 'WOMAN' ? '女' : '保密'}</div>
            )
        },
        {
            title: "状态",
            dataIndex: "status",
            search: false,
            align: "center",
            render: (text: string, record: any) => (
                <Switch
                    size="small"
                    checkedChildren="启用"
                    unCheckedChildren="禁用"
                    checked={text === 'ENABLE'}
                    onChange={(checked) => handleAccountStatusChange(record, checked ? 'ENABLE' : 'DISABLE')}
                />
            )
        },
        {
            title: "最后登录时间",
            dataIndex: "loginDate",
            search: false,
            align: "center",
        },
        {
            title: "密码修改时间",
            dataIndex: "pwdUpdateDate",
            search: false,
            align: "center",
        },
        {
            title: "创建时间",
            dataIndex: "createAt",
            search: false,
            align: "center",
            render: (text: any) => {
                return moment(text).format('YYYY-MM-DD HH:mm:ss');
            }
        },
        {
            title: "创建时间",
            dataIndex: "createAt",
            align: "center",
            search: true,
            hideInTable: true,
            valueType: "dateRange",
        },
        {
            title: "操作",
            key: 'operation',
            search: false,
            align: "center",
            fixed: 'right',
            width: 150,
            render: (_: any, record: any) => (
                <>
                    <Button type='link' size='small' onClick={() => handleAddOrEdit(record)}>
                        编辑
                    </Button>
                    <Button type='link' size='small' onClick={() => distributeRoles(record)}>
                        分配角色
                    </Button>
                    <Popconfirm title="确定重置密码？" okText="是" cancelText="否"
                        onConfirm={() => resetHandle(record.id)}>
                        <Button type='link' size='small'>重置密码</Button>
                    </Popconfirm>
                </>
            ),
        }
    ];

    const handleAccountStatusChange = (item: any, checked: any) => {
        jsonRequest.put(`/back/sys-user/change-status/${item.id}`, {})
            .then((resp: any) => resp.json())
            .then((data: any) => {
                if (data.code === 0) {
                    if (data.data.code) {
                        message.error(data.data.message);
                    } else {
                        message.success(`${checked === 'ENABLE' ? "启用" : "禁用"}成功`);
                        tableRef.current?.reload();
                    }
                } else {
                    message.error(data.msg);
                }
            }
            )
    }

    const handleAddOrEdit = (item: any) => {
        Modal.confirm({
            width: 500,
            title: !!item.id ? `用户编辑 - ${item.userName}` : "用户新增",
            content: <GlobalWrap><UserForm form={form} item={item} /></GlobalWrap>,
            onOk: async (close) => {
                let values = await form.validateFields();
                if (!!item?.id) {
                    await jsonRequest.put("/back/sys-user/modify", {
                        ...values,
                        id: item.id,
                    }).then((resp: any) => resp.json())
                        .then((data: any) => {
                            if (data?.code === 0) {
                                message.success("编辑成功");
                            } else {
                                message.error(data?.msg || "编辑失败");
                            }
                        })
                } else {
                    const checkResponse = await jsonRequest.get("/back/sys-user/check-username", {
                        userName: values.userName
                    });
                    const checkData = await checkResponse.json();

                    if (checkData?.code === 0) {
                        if (!checkData.data) {
                            message.error("用户名已存在");
                            return Promise.reject("用户名已存在");
                        }
                    } else {
                        message.error(checkData?.msg || "检查失败");
                        return Promise.reject("检查失败");
                    }

                    await jsonRequest.post("/back/sys-user/add", { ...values })
                        .then((resp: any) => resp.json())
                        .then((data: any) => {
                            if (data?.code === 0) {
                                message.success("新增成功");
                            } else {
                                message.error(data?.data?.[0]?.message || "新增失败");
                                return Promise.reject("新增失败");
                            }
                        });
                }
                tableRef.current?.reload();
                form.resetFields();
                close();
                return false;
            },
            onCancel: (close) => {
                form.resetFields();
                close();
            }
        })
    }

    const distributeRoles = (item: any) => {
        Modal.confirm({
            width: 500,
            title: "用户角色分配",
            content: <GlobalWrap><UserRoleForm form={form} item={item} /></GlobalWrap>,
            onOk: async (close) => {
                let values = await form.validateFields();
                await jsonRequest.put("/back/sys-user/modify", {
                    roleIds: values.roleIds,
                    id: item.id,
                }).then((resp: any) => resp.json())
                    .then((data: any) => {
                        if (data?.code === 0) {
                            message.success("编辑成功");
                        } else {
                            message.error(data?.msg || "编辑失败");
                        }
                    })
                tableRef.current?.reload();
                form.resetFields();
                close();
                return false;
            },
            onCancel: (close) => {
                form.resetFields();
                close();
            }
        })
    }

    const resetHandle = async (id: any) => {
        const result: any = await jsonRequest.put(`/back/sys-user/reset-pwd`, {
            id: id
        });
        if (result?.body.code === 0)
            message.success('重置密码成功,重置后密码为 用户名@Aa123 ');
        else {
            message.error('重置密码失败');
            console.log(result);
        }
        tableRef.current?.reload();
    }

    const init = async (url: any) => {
        const result = await jsonRequest.get(`/common/oss-file-visit-url`, { fileUrl: url })
            .then((resp: any) => resp.json());
        return result?.data;
    };

    const request = async (params: any) => {
        const result = await jsonRequest.get("/back/sys-user/page", {
            pageSize: params.pageSize,
            currentPage: params.current,
            status: params.status,
            queryBeginDate: params?.createAt?.[0],
            queryEndDate: params?.createAt?.[1],
        }).then((resp: any) => resp.json());
        const { data } = result;
        const processedData = await Promise.all(data?.data?.map(async (item: any) => {
            if (item.avatar) {
                const url = await init(item.avatar);
                return { ...item, url: url };
            }
            return item;
        }));
        return { data: processedData, success: result.code === 0, total: data.count };
    };

    return <>
        <h1>用户管理</h1>
        <ProTable
            rowKey="id"
            scroll={{ x: 1500 }}
            actionRef={tableRef}
            cardBordered
            bordered
            columns={columns}
            request={request}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
            search={{ span: 6, defaultCollapsed: false }}
            toolBarRender={() => [<Button
                key="button"
                icon={<PlusOutlined />}
                onClick={() => handleAddOrEdit({})}
                type="primary"
            >
                新增
            </Button>]}
        />
    </>;
}